import Image from "next/image";

import { FC, useState } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import Heading from "sections/nfcTagsPage/Heading";
import FeaturesModal from "sections/nfcTagsPage/FeaturesModal";

import CustomButton from "components/CustomButton";

import styles from "./styles";

interface FeaturesSectionProps {
  boxProps?: BoxProps;
}

export interface Card {
  id: number;
  title: string;
  description: string;
  details: string;
  image: string;
  imageModal?: string;
}

const CARDS: Card[] = [
  {
    id: 0,
    title: "Web3 and PoA",
    description:
      "Web3 is the new world of the Internet at the threshold of which we are now standing. It is based on blockchain technology and is secure and unalterable.",
    details:
      "Web3 means data security, better scalability, and greater privacy. With our solutions, you can authenticate your products, bringing them deeper into the digital world. This technology will change the fashion industry and allow you to turn any product into a smart connected one to build lasting relationships with customers and take care of the environment.  Uniqly's Proof of Authenticity and Ownership technologies verify credibility and ownership and will allow you to gain insight into the fast-growing pre-owned market by building a history of ownership of your products on the blockchain. PoA technology will secure your products against counterfeits.",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/0.png`,
    imageModal: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/0-modal.png`,
  },
  {
    id: 1,
    title: "NFC Labels",
    description:
      "Engage your customers with a single scan through NFC Labels containing all the necessary information.",
    details:
      "NFC an alternative to a QR code allows customers to interact with your products with just a single tap. A sewn-on NFC tag contains all the relevant information, such as product composition, date, and production history, as well as any promotional materials and other messages you want to convey to increase brand awareness and loyalty.",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/1.png`,
  },
  {
    id: 2,
    title: "E-commerce",
    description:
      "Think of e-commerce, but not as a simple online store. Think of your buyers becoming your ambassadors through the e-commerce module we have implemented.",
    details:
      "Your online store can function in a whole new capacity. By implementing personable referral links to every single product you sell, your customers become your ambassadors. That allows them to promote your products wherever they are. They are rewarded with discounts or tokens and you gain a new revenue channel.",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/2.png`,
  },
  {
    id: 3,
    title: "Loyalty & gamification",
    description:
      "Building loyalty among fans and giving them rewards through gamification are now at your fingertips.",
    details:
      "Conduct surveys asking for feedback, inform about upcoming promotions and news and create a loyalty program whereby a user gains specific points after purchase or recommendation. Survey customer satisfaction levels, and give them a small gift on their birthday. Let your customers purchase all  products from your collection to unlock special rewards or drops.",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/3.png`,
    imageModal: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/3-modal.png`,
  },
  {
    id: 4,
    title: "Upcoming EU regulations",
    description: "Stay one step ahead of new EU 2030 regulations.",
    details:
      "Upcoming EU legislation will oblige fashion brands to provide new labels with comprehensive information regarding circularity, sustainability, size, manufacturing country, fibre composition, and animal products. With our innovative solution, you can be compliant with the new regulations today!",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/4.png`,
    imageModal: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/4-modal.png`,
  },
  {
    id: 5,
    title: "User Extensions",
    description:
      'Discover the latest addition to the Uniqly experience - "My Extensions"! A brand new way to enhance your physical NFTs and unlock a whole new world of possibilities.',
    details:
      "With \"User Extensions\", every item on Uniqly comes to life like never before. Simply tap your phone to the scannable NFC tag sewn onto your item, and you'll be transported to a landing page that's packed with surprises and fresh utilities. \n" +
      '"User Extensions" let you turn on new features for your items and discover even more possibilities. Share the special experiences with your community by letting them scan the NFC tags on your physical clothes. \n' +
      'With "User Extensions", your items are transformed into unique and interactive experiences that are tailored to your needs. We\'re constantly working on new and innovative utilities for clothing, and "User Extensions" is just the beginning. Whether you\'re a fashion lover, a collector, or just someone who appreciates the value of a physical items backed by NFTs, "User Extensions" have something for everyone. So why wait? Explore the possibilities today and elevate your Uniqly experience to new heights. "User Extensions" - the future of fashion is here!',
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/5.png`,
    imageModal: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/features/5-modal.png`,
  },
];

const FeaturesSection: FC<FeaturesSectionProps> = ({ boxProps }) => {
  const [openedItemId, setOpenedItemId] = useState<number>(-1);
  const [isModalOpened, setModalOpened] = useState(false);

  const closeModal = () => {
    setModalOpened(false);
  };

  const onButtonClick = (id: number) => {
    setOpenedItemId(id);
    setModalOpened(true);
  };

  return (
    <Box component="section" {...boxProps}>
      <Heading heading="Features" />

      <Box sx={styles.wrapper}>
        {CARDS.map((card) => (
          <Box key={card.id} sx={styles.card}>
            <Typography variant="h2" component="h3" sx={styles.title}>
              {card.title}
            </Typography>
            <Typography paragraph sx={styles.description}>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  float: "right",
                  width: { xs: "40%", lg: "40%" },
                }}
              >
                <Image
                  src={card.image}
                  width={255}
                  height={330}
                  alt=""
                  style={{
                    width: "100%",
                    objectFit: "contain",
                    height: "auto",
                  }}
                />
              </Box>
              {card.description}
            </Typography>

            <CustomButton
              sx={styles.detailsButton}
              onClick={() => {
                onButtonClick(card.id);
              }}
            >
              See more
            </CustomButton>
          </Box>
        ))}
      </Box>

      <FeaturesModal
        isOpen={isModalOpened}
        handleClose={closeModal}
        item={CARDS[openedItemId]}
      />
    </Box>
  );
};

export default FeaturesSection;
